import { ReactElement } from "react";
import type { GetStaticProps, GetStaticPaths } from "next";
import NextHeadSeo from "next-head-seo";

import LayoutMain from "~/components/layouts/LayoutMain";
import LayoutIntroSlideshow from "~/components/layouts/LayoutIntroSlideshow";
import { Header } from "~/components/app/Header";
import dynamic from "next/dynamic";
import {
  restApiGetPostBySlugOrFallbackId,
  restApiESGetSettings,
  restApiESQuery,
} from "~/utils/restApi";
import { FlexibleRow } from "~/components/flexibleContent/FlexibleRow";
import { appConfig } from "~/config";
import { PageSubMenu } from "~/components/ui/PageSubMenu";
import styled from "styled-components";
import { Footer } from "~/components/app/Footer";
import { safeAnchorId } from "~/utils/safeAnchorId";
import {
  FRONTEND_NOT_FOUND_REVALIDATE_SECONDS,
  isBlockedFrontendPath,
} from "~/utils/requestGuards";

const MicroPageIntro = dynamic(
  () => import("~/components/content/MicroPageIntro")
);

const MicroPageIntroContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #000;

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        margin-bottom: ${props.theme.spacePx(breakpoint, 7)};
      `;
    })}
`;

const Page = ({ data }: { data: any }) => {
  const menuItems = data?.acf?.is_microsite
    ? data?.acf?.content?.length > 0
      ? data?.acf?.content.reduce((carry: any, row: any) => {
          if (row?.acf_fc_layout === "subpage") {
            carry.push({
              anchor: `#${safeAnchorId(row?.name)}`,
              title: row?.name,
            });
          }
          return carry;
        }, [])
      : []
    : data?.subMenu?.menu;

  return (
    <>
      <NextHeadSeo
        canonical={data?.yoast_head_json?.canonical}
        title={data?.yoast_head_json?.title ?? data?.title}
        description={data?.yoast_head_json?.description}
        og={{
          title: data?.yoast_head_json?.og_title,
          type: data?.yoast_head_json?.og_type,
          siteName: data?.yoast_head_json?.og_site_name,
          image: data?.yoast_head_json?.twitter_image,
        }}
        twitter={{
          card: "summary_large_image",
        }}
      />
      <Header
        hasHero={data?.acf?.is_microsite}
        showMainMenu={!data?.acf?.is_microsite}
      >
        <PageSubMenu title={data?.title} menu={menuItems} />
      </Header>

      {data?.acf?.is_microsite && (
        <Header isOverlay showMainMenu={!data?.acf?.is_microsite} />
      )}

      <div id="content" style={{ position: "absolute" }} />
      {data?.acf?.is_microsite && (
        <MicroPageIntroContainer>
          <MicroPageIntro data={data} menuItems={menuItems} />
        </MicroPageIntroContainer>
      )}
      <main>
        {data?.acf?.content?.length > 0 ? (
          <FlexibleRow
            field="content"
            content={data?.acf?.content}
            fontStyle={data?.acf?.font_style ?? "default"}
          />
        ) : (
          <div />
        )}
      </main>
      {data?.acf?.is_microsite && <Footer />}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const pages = await restApiESQuery({
    type: "page",
    perPage: 50,
    orderby: "post_date",
    order: "desc",
  });

  return {
    paths: pages?.data?.length
      ? pages?.data.reduce((carry: string[], page: any) => {
          if (
            page?.url.indexOf("/home/") === -1 &&
            page?.url.indexOf("/connect/") === -1 &&
            page?.url.indexOf("/media/") === -1 &&
            page?.url.indexOf("/whats-on/") === -1 &&
            page?.url.indexOf("/news/") === -1 &&
            page?.url.indexOf("/archive/") === -1
          )
            carry.push(page.url);

          return carry;
        }, [])
      : [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const token = (context?.previewData as any)?.token;
  const slugSegments = Array.isArray(context?.params?.slug)
    ? context?.params?.slug
    : context?.params?.slug
    ? [context.params.slug as string]
    : [];

  if (isBlockedFrontendPath(slugSegments)) {
    return {
      notFound: true,
      revalidate: FRONTEND_NOT_FOUND_REVALIDATE_SECONDS,
    };
  }

  const slug = context?.params?.slug
    ? Array.isArray(context?.params?.slug) && context?.params?.slug?.length
      ? (context?.params?.slug?.pop() as string)
      : (context?.params?.slug as string)
    : 1;

  const data = await restApiGetPostBySlugOrFallbackId("page", slug, token);

  if (!data)
    return {
      notFound: true,
      revalidate: FRONTEND_NOT_FOUND_REVALIDATE_SECONDS,
    };

  const frontendSettings = await restApiESGetSettings();

  return {
    props: {
      frontendSettings,
      data: data ?? null,
    },
    revalidate: frontendSettings?.options?.copyrightNotice
      ? appConfig.revalidateInterval("page", {
          date: data?.date,
          modified: data?.modified,
        })
      : 15,
  };
};

Page.getLayout = function getLayout(page: ReactElement, props: any) {
  if (props?.data?.acf?.is_microsite)
    return <LayoutIntroSlideshow props={props}>{page}</LayoutIntroSlideshow>;
  return <LayoutMain props={props}>{page}</LayoutMain>;
};
export default Page;
